import DocumentTable, { ActionAddDocuments } from 'components/DocumentTable';
import {
  ENTITY_EXPERIMENT,
  ENTITY_NOTE,
  ENTITY_SOP,
  ENTITY_TASK,
} from 'constants/schemas';
import React, { useCallback, useEffect, useMemo } from 'react';
import { SPACE_DEPARTMENT, SPACE_TEAM, SPACE_USER } from 'constants/space.constants';
import { directoryActions, entitySelectors } from 'store/entity';
import { formatDate, momentValue } from 'utils/date.utils';
import { genericIdFromString, genericIdToString, getGenericId } from 'utils/generic.utils';
import { isNil, maxBy, zip } from 'lodash';
import { useDirectoryItemList, useEntity, useFetchListing, useSpaceGenericId, useSpaceType } from 'hooks/store.hooks';

import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getPath } from 'utils/entity.utils';
import { useNavigate } from 'react-router-dom';

const contentTypes = [ENTITY_EXPERIMENT, ENTITY_NOTE, ENTITY_SOP, ENTITY_TASK];
const templatesInitState = Object.fromEntries(contentTypes.map((type) => [type, { is_template: true }]));

function AllDocumentsTable(props) {
  const { listView, selected, setSelected, templates } = props;
  const navigate = useNavigate();
  const spaceType = useSpaceType();
  const spaceGenericId = useSpaceGenericId();

  const fetchOptions = useMemo(() => ({
    params: { filters: {
      content_types: contentTypes,
      is_template: templates,
      ...(spaceType === SPACE_USER ? { created_by: spaceGenericId.id } : {}),
      ...(spaceType === SPACE_TEAM ? { team: spaceGenericId.id } : {}),
      ...(spaceType === SPACE_DEPARTMENT ? { department: spaceGenericId.id } : {}),
    }},
  }), [spaceType, spaceGenericId, templates]);

  const listParams = useMemo(() => ({
    prop: { id: 'object_id', type: 'content_type' },
    filter: (directoryItem, state) => {
      const genericId = getGenericId(directoryItem.object_id, directoryItem.content_type);
      return (
        contentTypes.includes(directoryItem.content_type) &&
        (entitySelectors.selectEntity(state, genericId, 'is_template') ?? false) === templates &&
        entitySelectors.selectEntityInSpace(state, genericId, spaceType, spaceGenericId)
      );
    },
  }), [spaceType, spaceGenericId, templates]);

  const { loading } = useFetchListing(directoryActions.fetchDirectoryItems, fetchOptions);
  const genericIds = useDirectoryItemList(listParams);

  const createdDates = useEntity(genericIds, 'created');
  useEffect(() => {
    if (isNil(selected)) {
      const maxPair = maxBy(zip(genericIds, createdDates), (p) => momentValue(p[1]));
      setSelected(genericIdToString(maxPair?.[0]), { inParams: false });
    }
  }, [selected, setSelected, genericIds, createdDates]);

  const listViewOnRowClick = useCallback((row, event) => {
    const genericId = genericIdFromString(row.id);
    if (event.detail > 1) {
      navigate(getPath(genericId));
    } else {
      setSelected(row.id, { inParams: true });
    }
  }, [navigate, setSelected]);

  const listViewColumnProps = useMemo(() => ({
    checkbox: {
      selection: isNil(selected) ? [] : [selected],
    },
    name: {
      renderValue: ({ row, value }) => (
        <Stack>
          <Typography variant='subtitle2'>{value || 'Untitled'}</Typography>
          <Typography color='text.secondary' variant='caption'>{formatDate(row.created)}</Typography>
        </Stack>
      ),
      cellProps: { sx: { whiteSpace: 'normal' }},
    },
    owner: { hidden: true },
    status: { hidden: true },
    created: { hidden: true },
  }), [selected]);

  const tableViewColumnProps = useMemo(() => ({
    status: { hidden: templates },
  }), [templates]);

  return (
    <DocumentTable
      ids={genericIds}
      loading={loading}
      initState={{
        groupBy: listView || templates ? null : 'created',
        groupDatesBy: 'natural',
      }}
      {...(listView ? { onRowClick: listViewOnRowClick } : {})}
      noHeader={listView}
      disableFilter={listView}
      disableGroupBy={listView}
      columnProps={listView ? listViewColumnProps : tableViewColumnProps}
      actions={listView ? null : (
        <ActionAddDocuments
          entityTypes={contentTypes}
          initStateMap={templates ? templatesInitState : null}
        />
      )}
      sx={{ overflowY: 'auto' }}
    />
  );
}

AllDocumentsTable.propTypes = {
  listView: PropTypes.bool,
  selected: PropTypes.string,
  setSelected: PropTypes.func,
  templates: PropTypes.bool,
};

export default AllDocumentsTable;
